import React from 'react';
import {
	useDispatch,
} from 'react-redux';

import type CK from '~/types/contentking';

import NavigationLayoutToggle from '~/components/app/NavigationLayoutToggle';
import NavigationMinimizeToggle from '~/components/app/NavigationMinimizeToggle';

import {
	buildNewSecondaryItems,
} from './builders/buildNewSecondaryItems';
import useNewPrimaryItems from './builders/useNewPrimaryItems';
import OverlayScrollbar from '~/components/patterns/scrollbars/OverlayScrollbar';

import {
	menu,
} from '~/actions';

import {
	navigate,
} from '~/routing/router';

import {
	type messages
} from './MainNavigation';
import useAutoExpandNavigation from '~/hooks/useAutoExpandNavigation';
import useNavigationAuthConfig from '~/hooks/useNavigationAuthConfig';



type Props = {
	accountId: CK.AccountId | null,
	areSecondaryNavItemsHidden: boolean,
	areTechnicalSubItemsHidden: boolean,
	effectiveHomeAccountId: CK.AccountId | null,
	hasMultipleAccounts: boolean,
	hideWebsitesToggle: boolean,
	isAccountExpanded: boolean,
	isContentExpanded: boolean,
	isCurrentlyPhase2: boolean,
	isInternalExpanded: boolean,
	isPerformanceExpanded: boolean,
	isTechnicalExpanded: boolean,
	kingdomAdminFeatures: {
		areAlwaysVisible?: boolean,
		areVisible?: boolean,
	},
	messages: typeof messages,
	openSwitchAccountModal: () => void,
	selectedAccountId: CK.AccountId | null,
	setAreSecondaryNavItemsHidden: (hidden: boolean) => void,
	setAreTechnicalSubItemsHidden: (hidden: boolean) => void,
	setAccountExpanded: (expanded: boolean) => void,
	setContentExpanded: (expanded: boolean) => void,
	setInternalExpanded: (expanded: boolean) => void,
	setPerformanceExpanded: (expanded: boolean) => void,
	setSelectedAccountId: (accountId: CK.AccountId | null) => void,
	setTechnicalExpanded: (expanded: boolean) => void,
	showPhase2CompactLayout: boolean,
	showToggle: boolean,
	technicalSectionItems: Array<React.ReactElement | null>,
	websiteAccountName: string | null,
	websiteId: CK.WebsiteId | null,
	websitesNavToggleRef: React.RefObject<HTMLLIElement>,
};

/**
 * New Navigation Component
 *
 * Renders the new (dark + green) navigation layout structure.
 * This component builds navigation items and handles the wrapper, list structure, and toggle buttons.
 */
const NewNavigation: React.FC<Props> = (props) => {
	const {
		accountId,
		areSecondaryNavItemsHidden,
		areTechnicalSubItemsHidden,
		effectiveHomeAccountId,
		hasMultipleAccounts,
		hideWebsitesToggle,
		isAccountExpanded,
		isContentExpanded,
		isCurrentlyPhase2,
		isInternalExpanded,
		isPerformanceExpanded,
		isTechnicalExpanded,
		kingdomAdminFeatures,
		messages,
		openSwitchAccountModal,
		selectedAccountId,
		setAreSecondaryNavItemsHidden,
		setAreTechnicalSubItemsHidden,
		setAccountExpanded,
		setContentExpanded,
		setInternalExpanded,
		setPerformanceExpanded,
		setSelectedAccountId,
		setTechnicalExpanded,
		showPhase2CompactLayout,
		showToggle,
		technicalSectionItems,
		websiteId,
	} = props;

	const dispatch = useDispatch();
	const handleAutoExpandNavigation = useAutoExpandNavigation();
	const authConfig = useNavigationAuthConfig();

	const closeWebsiteSwitcher = React.useCallback(
		() => {
			dispatch(menu.closeWebsiteSwitcher());
		},
		[dispatch],
	);

	// Click handlers for Phase 2 compact layout icons
	const handleWebsitesIconClick = React.useCallback(() => {
		// 1. Expand navigation to Phase 3 first
		handleAutoExpandNavigation();
		// Ensure Sub-items are visible (in case they were hidden)
		setAreTechnicalSubItemsHidden(false);
		setAreSecondaryNavItemsHidden(false);

		// 2. Collapse websites dropdown so its contents are visible (search box and websites list)
		// Set selectedAccountId to show the inline websites section
		setSelectedAccountId(effectiveHomeAccountId);
		// Also open the old dropdown state for consistency
		dispatch(menu.openWebsiteSwitcher());
	}, [handleAutoExpandNavigation, dispatch, effectiveHomeAccountId, setSelectedAccountId, setAreTechnicalSubItemsHidden, setAreSecondaryNavItemsHidden]);

	const handleSearchIconClick = React.useCallback(() => {
		// 1. Expand navigation to Phase 3 first
		handleAutoExpandNavigation();
		// Ensure Sub-items are visible (in case they were hidden)
		setAreTechnicalSubItemsHidden(false);
		setAreSecondaryNavItemsHidden(false);

		// 2. Collapse websites dropdown so its contents are visible (search box and websites list)
		// Set selectedAccountId to show the inline websites section
		setSelectedAccountId(effectiveHomeAccountId);
		// Also open the old dropdown state for consistency
		dispatch(menu.openWebsiteSwitcher());
	}, [handleAutoExpandNavigation, dispatch, effectiveHomeAccountId, setSelectedAccountId, setAreTechnicalSubItemsHidden, setAreSecondaryNavItemsHidden]);

	// Collapse all expandables when clicking a standalone item (so only that item shows green)
	// Also reset hidden states so the full navigation is visible (e.g. after viewing Accounts list)
	const collapseAllExpandables = React.useCallback(() => {
		setAccountExpanded(false);
		setContentExpanded(false);
		setPerformanceExpanded(false);
		setTechnicalExpanded(false);
		setInternalExpanded(false);
		setAreTechnicalSubItemsHidden(false);
		setAreSecondaryNavItemsHidden(false);
	}, [setAccountExpanded, setContentExpanded, setPerformanceExpanded, setTechnicalExpanded, setInternalExpanded, setAreTechnicalSubItemsHidden, setAreSecondaryNavItemsHidden]);

	// Collapse other expandables but keep Account expanded (for Account sub-items like Manage My Profile)
	const collapseExpandablesExceptAccount = React.useCallback(() => {
		setContentExpanded(false);
		setPerformanceExpanded(false);
		setTechnicalExpanded(false);
		setInternalExpanded(false);
	}, [setContentExpanded, setPerformanceExpanded, setTechnicalExpanded, setInternalExpanded]);

	const onAccountToggle = React.useCallback(() => {
		handleAutoExpandNavigation();
		const willExpand = !isAccountExpanded;
		setAccountExpanded(willExpand);
		if (willExpand) {
			setContentExpanded(false);
			setPerformanceExpanded(false);
			setTechnicalExpanded(false);
			setInternalExpanded(false);
			navigate('account.websites', {
				accountId: accountId ?? effectiveHomeAccountId ?? undefined,
				view: 'list',
			});
		}
	}, [handleAutoExpandNavigation, isAccountExpanded, setAccountExpanded, setContentExpanded, setPerformanceExpanded, setTechnicalExpanded, setInternalExpanded, accountId, effectiveHomeAccountId]);

	const onInternalToggle = React.useCallback(() => {
		handleAutoExpandNavigation();
		const willExpand = !isInternalExpanded;
		setInternalExpanded(willExpand);
		if (willExpand) {
			setAccountExpanded(false);
			setContentExpanded(false);
			setPerformanceExpanded(false);
			setTechnicalExpanded(false);
			navigate('generalAdmin', {});
		}
	}, [handleAutoExpandNavigation, isInternalExpanded, setInternalExpanded, setAccountExpanded, setContentExpanded, setPerformanceExpanded, setTechnicalExpanded]);

	// Build primary navigation items using hook
	const newPrimaryItems = useNewPrimaryItems({
		areSecondaryNavItemsHidden,
		areTechnicalSubItemsHidden,
		closeWebsiteSwitcher,
		collapseAllExpandables,
		effectiveHomeAccountId,
		handleAutoExpandNavigation,
		setAccountExpanded,
		setInternalExpanded,
		handleSearchIconClick,
		handleWebsitesIconClick,
		hasMultipleAccounts,
		hideWebsitesToggle,
		isContentExpanded,
		isCurrentlyPhase2,
		isPerformanceExpanded,
		isTechnicalExpanded,
		messages,
		selectedAccountId,
		setAreSecondaryNavItemsHidden,
		setAreTechnicalSubItemsHidden,
		setContentExpanded,
		setPerformanceExpanded,
		setSelectedAccountId,
		setTechnicalExpanded,
		showPhase2CompactLayout,
		technicalSectionItems,
	});

	// Build secondary navigation items
	// When any expandable is expanded, suppress route-based active state so only the expanded header shows green
	const isAnyExpandableExpanded = isAccountExpanded || isContentExpanded || isPerformanceExpanded || isTechnicalExpanded || isInternalExpanded;
	const newSecondaryItems = React.useMemo(
		() => buildNewSecondaryItems({
			accountId,
			collapseAllExpandables,
			collapseExpandablesExceptAccount,
			effectiveHomeAccountId,
			showIntegrations: authConfig.authType === 'sso',
			isAccountExpanded,
			isAnyExpandableExpanded,
			isInternalExpanded,
			kingdomAdminFeatures,
			messages,
			onAccountToggle,
			onInternalToggle,
			openSwitchAccountModal,
			websiteId,
		}),
		[
			accountId,
			collapseAllExpandables,
			collapseExpandablesExceptAccount,
			effectiveHomeAccountId,
			authConfig.authType,
			isAccountExpanded,
			isAnyExpandableExpanded,
			isInternalExpanded,
			kingdomAdminFeatures,
			messages,
			onAccountToggle,
			onInternalToggle,
			openSwitchAccountModal,
			websiteId,
		],
	);

	// Combine all navigation items into one list
	const allNavItems = React.useMemo(
		() => [
			...newPrimaryItems,
			...newSecondaryItems,
		],
		[newPrimaryItems, newSecondaryItems],
	);

	return (
		<>
			<div className="main-nav__wrapper">
				<OverlayScrollbar
					className="main-nav__overlay-scrollbar-root"
					viewportClassName="main-nav__scroll-viewport"
				>
					<nav
						aria-label="Main navigation"
						className="main-nav"
						role="navigation"
					>
						<ul className={areSecondaryNavItemsHidden && !isCurrentlyPhase2 ? 'main-nav__list main-nav__list--hide-secondary-items' : 'main-nav__list'}>
							{allNavItems.map((item, index) => {
								// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
								if (!item) {
									return null;
								}

								return (
									<React.Fragment key={`new-nav-item-${index}`}>
										{item}
									</React.Fragment>
								);
							})}
						</ul>
					</nav>
				</OverlayScrollbar>
				{/* Toggle button at the very bottom of navbar */}
				{/* Hide divider when account list is shown (avoids double divider below Accounts Overview) */}
				{showToggle && !(areSecondaryNavItemsHidden && !isCurrentlyPhase2) && (
					<div className="main-nav__divider-wrapper">
						<div className="main-nav__divider" />
					</div>
				)}
				{showToggle && (
					<div className="sidebar__navigation-layout-toggle">
						<NavigationLayoutToggle />
					</div>
				)}
				{/* Minimize/Keep open toggle button - only for new navigation */}
				<div className="sidebar__navigation-minimize-toggle">
					<NavigationMinimizeToggle />
				</div>
			</div>
		</>
	);
};

export default NewNavigation;
